<template>
  <section v-if="items.length" id="story-timeline" class="story-timeline" aria-labelledby="story-timeline-title">
    <v-container>
      <p class="section-kicker">The Trip in Order</p>
      <h2 id="story-timeline-title">Follow the Story Chronologically</h2>
      <p class="section-description">
        Choose a {{ groupNoun }}, then move through each meal, session, activity, and stop in order.
      </p>

      <div class="day-tabs" role="tablist" :aria-label="`Choose a journey ${groupNoun}`">
        <button
          v-for="(day, dayIndex) in days"
          :id="`journey-day-tab-${dayIndex}`"
          :key="day.id"
          type="button"
          role="tab"
          :aria-selected="dayIndex === activeDayIndex ? 'true' : 'false'"
          :aria-controls="`journey-day-panel-${dayIndex}`"
          :disabled="day.disabled"
          :class="{ active: dayIndex === activeDayIndex }"
          @click="selectDay(dayIndex)"
        >
          <strong>{{ day.shortLabel }}</strong>
          <span v-if="day.dateLabel">{{ day.dateLabel }}</span>
        </button>
      </div>

      <div
        v-if="activeItem"
        :id="`journey-day-panel-${activeDayIndex}`"
        class="journey-viewer"
        role="tabpanel"
        :aria-labelledby="`journey-day-tab-${activeDayIndex}`"
      >
        <div class="map-panel">
          <div class="map-toolbar">
            <div class="map-view-toggle" aria-label="Choose map view">
              <button
                type="button"
                :class="{ active: mapView === 'stops' }"
                @click="mapView = 'stops'"
              >
                {{ hasMappedStops ? 'Numbered map' : 'Location map' }}
              </button>
              <button
                v-if="directionsAvailable"
                type="button"
                :class="{ active: mapView === 'directions' }"
                @click="mapView = 'directions'"
              >
                {{ directionsToggleLabel }}
              </button>
            </div>
          </div>
          <div
            v-if="mapView === 'directions' && travelSegments.length > 1"
            class="direction-segments"
            role="tablist"
            aria-label="Choose a travel segment"
          >
            <button
              v-for="(segment, segmentIndex) in travelSegments"
              :key="segment.id"
              type="button"
              role="tab"
              :aria-selected="segmentIndex === activeTravelSegmentIndex ? 'true' : 'false'"
              :class="[`mode-${segment.mode}`, { active: segmentIndex === activeTravelSegmentIndex }]"
              @click="selectTravelSegment(segmentIndex)"
            >
              {{ segment.label }}
            </button>
          </div>
          <div class="map-stage">
            <div
              v-show="mapView === 'stops' && hasMappedStops"
              ref="journeyMap"
              class="numbered-map"
              :title="`Map of ${activeDay.fullLabel} journey stops`"
            ></div>
            <iframe
              v-if="mapView === 'directions' || !hasMappedStops"
              :key="`${activeDay.id}-${mapView}-${activeTravelSegmentIndex}-${hasMappedStops ? '' : activeItem.id}`"
              :title="mapView === 'directions' ? `${activeTravelSegment.label} directions for ${activeDay.fullLabel}` : `Location of ${activeItem.title}`"
              :src="mapView === 'directions' ? dayMapEmbedUrl : locationMapEmbedUrl"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
            <div v-if="mapView === 'stops' && hasMappedStops && unmappedStops.length" class="unmapped-stops">
              <span>Location needed:</span>
              <button
                v-for="marker in unmappedStops"
                :key="marker.item.id"
                type="button"
                @click="selectItem(marker.index)"
              >
                {{ marker.index + 1 }}
              </button>
            </div>
            <div class="map-label">
              <v-icon small>mdi-map-marker</v-icon>
              <span>{{ mapPanelLabel }}</span>
            </div>
          </div>
        </div>

        <article class="active-story" :class="{ 'draft-preview': activeItem.status === 'draft' }" aria-live="polite">
          <div class="story-progress">
            <span>Stop {{ activeItemIndex + 1 }} of {{ activeDay.items.length }}</span>
            <span>{{ activeDay.fullLabel }}</span>
          </div>
          <p class="story-type">{{ activeItem.type }}</p>
          <DraftBadge v-if="activeItem.status === 'draft'" />
          <h3>{{ activeItem.title }}</h3>
          <p v-if="activeItem.timeLabel" class="story-summary">{{ activeItem.timeLabel }}</p>
          <p v-if="activeItem.summary" class="story-summary">{{ activeItem.summary }}</p>
          <p v-if="activeItem.mapPrecisionNote" class="story-summary">{{ activeItem.mapPrecisionNote }}</p>
          <div v-if="activeItem.websiteUrl || activeItem.venueRoute || activeItem.route" class="story-links">
            <router-link v-if="activeItem.venueRoute" :to="activeItem.venueRoute" class="story-link">
              Venue Details <v-icon x-small>mdi-map-marker-outline</v-icon>
            </router-link>
            <a
              v-if="activeItem.websiteUrl"
              :href="activeItem.websiteUrl"
              class="story-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Website <v-icon x-small>mdi-open-in-new</v-icon>
            </a>
            <router-link v-if="activeItem.route" :to="activeItem.route" class="story-link">
              Read the Story <v-icon x-small>mdi-arrow-right</v-icon>
            </router-link>
          </div>
          <div class="journey-controls">
            <button type="button" :disabled="!hasPrevious" @click="goPrevious">
              <v-icon small>mdi-arrow-left</v-icon> Previous
            </button>
            <button type="button" :disabled="!hasNext" @click="goNext">
              {{ nextLabel }} <v-icon small>mdi-arrow-right</v-icon>
            </button>
          </div>
        </article>
      </div>

      <ol v-if="activeDay" class="day-route" :aria-label="`${activeDay.fullLabel} journey stops`">
        <li v-for="(item, index) in activeDay.items" :key="item.id">
          <button
            :id="`story-${item.id}`"
            type="button"
            :class="{ active: index === activeItemIndex }"
            :aria-current="index === activeItemIndex ? 'step' : null"
            @click="selectItem(index)"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ item.title }}</strong>
            <DraftBadge v-if="item.status === 'draft'" />
          </button>
        </li>
      </ol>
    </v-container>
  </section>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { eventEntityRoute } from '@/lib/reckon-way/entityDetails';
import { eventStoryTimeline } from '@/lib/reckon-way/storyTimeline';
import { hasMapCoordinates, journeyLocationEmbedUrl } from '@/lib/reckon-way/journeyMap';
import DraftBadge from '@/components/draft/DraftBadge.vue';

export default {
  name: 'TripStoryTimeline',
  components: { DraftBadge },
  props: {
    event: { type: Object, required: true },
    entityById: { type: Object, required: true },
    groupBy: { type: String, default: 'day' },
  },
  data: () => ({
    activeDayIndex: 0,
    activeItemIndex: 0,
    activeTravelSegmentIndex: 0,
    mapView: 'stops',
  }),
  computed: {
    items() {
      return eventStoryTimeline(
        this.event,
        this.entityById,
        this.$store.getters.draftModeEnabled,
        this.$store.getters.isAuthorizedReckonUser,
      );
    },
    groupNoun() { return this.groupBy === 'year' ? 'year' : 'day'; },
    days() {
      const groups = [];
      const previewAllDays = this.$store.getters.draftModeEnabled
        && this.$store.getters.isAuthorizedReckonUser;
      const disabledDayIds = new Set(previewAllDays ? [] : this.event.chronologyDisabledDates || []);
      this.items.forEach((item) => {
        const itemDate = item.journeyDate || item.startDate || '';
        const itemYear = item.year || (itemDate ? itemDate.slice(0, 4) : '');
        const id = this.groupBy === 'year'
          ? itemYear || item.dayLabel || 'journey'
          : itemDate || item.dayLabel || 'journey';
        let day = groups.find((entry) => entry.id === id);
        if (!day) {
          day = {
            id,
            fullLabel: this.groupBy === 'year' ? String(id) : this.fullDayLabel(item),
            shortLabel: this.groupBy === 'year' ? String(id) : this.shortDayLabel(item),
            dateLabel: this.groupBy === 'year' ? '' : this.shortDateLabel(item),
            disabled: disabledDayIds.has(String(id)),
            items: [],
          };
          groups.push(day);
        }
        day.items.push(item);
      });
      return groups.map((day) => {
        const hotelConfig = this.event.chronologyHotelsByDate?.[day.id];
        if (!hotelConfig) return day;
        const hasBoundaryConfig = typeof hotelConfig === 'object';
        const startHotelId = hasBoundaryConfig
          && Object.prototype.hasOwnProperty.call(hotelConfig, 'start')
          ? hotelConfig.start
          : hotelConfig;
        const endHotelId = hasBoundaryConfig
          && Object.prototype.hasOwnProperty.call(hotelConfig, 'end')
          ? hotelConfig.end
          : hotelConfig;
        const startHotel = this.hotelBoundaryItem(day, 'start', startHotelId);
        const endHotel = this.hotelBoundaryItem(day, 'end', endHotelId);
        return { ...day, items: [startHotel, ...day.items, endHotel].filter(Boolean) };
      });
    },
    activeDay() { return this.days[this.activeDayIndex] || null; },
    activeItem() { return this.activeDay?.items[this.activeItemIndex] || null; },
    fallbackLocation() {
      return [this.event.city, this.event.country].filter(Boolean).join(', ') || this.event.destination;
    },
    dayDefaultTravelMode() {
      return this.event.chronologyTravelModes?.[this.activeDay?.id]
        || this.event.chronologyTravelMode
        || 'walking';
    },
    travelLegs() {
      const items = this.activeDay?.items || [];
      const configured = this.event.chronologyTravelSegmentsByDate?.[this.activeDay?.id] || [];
      return items.slice(0, -1).map((from, index) => {
        const to = items[index + 1];
        const config = configured.find((entry) => (
          this.itemMatchesTravelKey(from, entry.from)
          && this.itemMatchesTravelKey(to, entry.to)
        )) || {};
        return {
          id: `${from.id}-${to.id}`,
          from,
          to,
          fromIndex: index,
          toIndex: index + 1,
          mode: config.mode || this.dayDefaultTravelMode,
          breakBefore: Boolean(config.breakBefore),
          routePoints: [from, ...(config.waypoints || []), to],
        };
      });
    },
    travelSegments() {
      return this.travelLegs.reduce((segments, leg) => {
        const previous = segments[segments.length - 1];
        if (previous && previous.mode === leg.mode && !leg.breakBefore) {
          previous.to = leg.to;
          previous.toIndex = leg.toIndex;
          previous.items.push(leg.to);
          previous.routePoints.push(...leg.routePoints.slice(1));
          previous.id = `${previous.from.id}-${leg.to.id}-${leg.mode}`;
          previous.label = this.travelSegmentLabel(previous);
          return segments;
        }
        const segment = {
          id: `${leg.id}-${leg.mode}`,
          mode: leg.mode,
          from: leg.from,
          to: leg.to,
          fromIndex: leg.fromIndex,
          toIndex: leg.toIndex,
          items: [leg.from, leg.to],
          routePoints: [...leg.routePoints],
        };
        segment.label = this.travelSegmentLabel(segment);
        segments.push(segment);
        return segments;
      }, []);
    },
    activeTravelSegment() {
      return this.travelSegments[this.activeTravelSegmentIndex]
        || this.travelSegments[0]
        || {
          id: 'journey',
          mode: this.dayDefaultTravelMode,
          label: this.travelModeName(this.dayDefaultTravelMode),
          items: this.activeDay?.items || [],
        };
    },
    activeTravelMode() {
      return this.activeTravelSegment.mode || this.dayDefaultTravelMode;
    },
    mapPanelLabel() {
      if (this.mapView === 'directions') return this.activeTravelSegment.label;
      return this.activeItem.mapLocation || this.fallbackLocation;
    },
    travelModeLabel() {
      return this.travelModeName(this.activeTravelMode);
    },
    directionsToggleLabel() {
      const modes = new Set(this.travelSegments.map((segment) => segment.mode));
      return modes.size > 1 ? 'Mixed directions' : `${this.travelModeLabel} directions`;
    },
    directionsAvailable() {
      return !(this.event.chronologyDirectionsDisabledDates || [])
        .includes(String(this.activeDay?.id));
    },
    travelModeFlag() {
      const flags = {
        bicycling: 'b',
        driving: 'd',
        ferry: 'r',
        'two-super-jeeps': 'd',
        snowmobile: 'd',
        'snowmobile-two-super-jeeps': 'd',
        transit: 'r',
        walking: 'w',
      };
      return flags[this.activeTravelMode] || 'w';
    },
    directionsMapEmbedUrl() {
      const stops = (this.activeTravelSegment.routePoints
        || this.activeTravelSegment.items
        || []).map((item) => {
        if (Number.isFinite(item.latitude) && Number.isFinite(item.longitude)) {
          return `${item.latitude},${item.longitude}`;
        }
        return [item.title, item.mapLocation || this.fallbackLocation].filter(Boolean).join(', ');
      });
      if (stops.length <= 1) {
        return `https://www.google.com/maps?q=${encodeURIComponent(stops[0] || this.fallbackLocation)}&output=embed`;
      }
      const destination = stops.slice(1).join(' to:');
      return `https://maps.google.com/maps?saddr=${encodeURIComponent(stops[0])}&daddr=${encodeURIComponent(destination)}&dirflg=${this.travelModeFlag}&output=embed`;
    },
    dayMapEmbedUrl() {
      return this.directionsMapEmbedUrl;
    },
    unmappedStops() {
      return (this.activeDay?.items || []).map((item, index) => ({ item, index })).filter(({ item }) => (
        !Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)
      ));
    },
    hasMappedStops() {
      return (this.activeDay?.items || []).some(hasMapCoordinates);
    },
    locationMapEmbedUrl() {
      return journeyLocationEmbedUrl(this.activeItem || {}, this.fallbackLocation);
    },
    hasPrevious() {
      return this.activeItemIndex > 0 || this.enabledDayIndex(this.activeDayIndex, -1) >= 0;
    },
    hasNext() {
      return this.activeItemIndex < (this.activeDay?.items.length || 0) - 1
        || this.enabledDayIndex(this.activeDayIndex, 1) >= 0;
    },
    nextLabel() {
      return this.activeItemIndex < (this.activeDay?.items.length || 0) - 1
        ? 'Next stop'
        : `Next ${this.groupNoun}`;
    },
  },
  watch: {
    '$route.hash': {
      immediate: true,
      handler() { this.selectFromHash(); },
    },
    activeDayIndex() {
      this.activeTravelSegmentIndex = 0;
      this.scheduleMapRender();
    },
    activeItemIndex() {
      this.syncTravelSegment();
      this.scheduleMapRender();
    },
    mapView(view) {
      if (view === 'stops') this.scheduleMapRender();
    },
  },
  mounted() { this.scheduleMapRender(); },
  beforeDestroy() {
    if (this.journeyMapInstance) this.journeyMapInstance.remove();
  },
  methods: {
    itemMatchesTravelKey(item, key) {
      if (!key) return false;
      return [item.id, item.chronologyId, item.recordId, item.title].includes(key);
    },
    travelModeName(mode) {
      const labels = {
        bicycling: 'Bike',
        driving: 'Drive',
        ferry: 'Ferry',
        'two-super-jeeps': '2 Super Jeeps',
        snowmobile: 'Snowmobile',
        'snowmobile-two-super-jeeps': 'Snowmobile + 2 Super Jeeps',
        transit: 'Transit',
        walking: 'Walk',
      };
      return labels[mode] || 'Walk';
    },
    travelSegmentLabel(segment) {
      return `${this.travelModeName(segment.mode)}: ${segment.from.title} → ${segment.to.title}`;
    },
    selectTravelSegment(index) {
      this.activeTravelSegmentIndex = index;
    },
    syncTravelSegment() {
      if (!this.travelSegments.length) {
        this.activeTravelSegmentIndex = 0;
        return;
      }
      const segmentIndex = this.travelSegments.findIndex((segment, index) => (
        this.activeItemIndex >= segment.fromIndex
        && (this.activeItemIndex < segment.toIndex || index === this.travelSegments.length - 1)
      ));
      this.activeTravelSegmentIndex = segmentIndex >= 0 ? segmentIndex : 0;
    },
    hotelBoundaryItem(day, phase, entityId) {
      const entity = this.entityById[entityId];
      const reference = (this.event.hotels || []).find((item) => item.entityId === entityId);
      if (!entity || !reference) return null;
      const isStart = phase === 'start';
      return {
        id: `hotel-${phase}-${day.id}-${entityId}`,
        recordId: entityId,
        sectionKey: 'hotels',
        type: 'Hotel',
        title: `${entity.name} (${isStart ? 'Start' : 'End'})`,
        summary: isStart
          ? 'The home base at the beginning of this day’s route.'
          : 'The home base at the end of this day’s route.',
        mapLocation: [entity.address, entity.city, entity.country].filter(Boolean).join(', '),
        latitude: entity.latitude,
        longitude: entity.longitude,
        route: eventEntityRoute(this.event, 'hotels', reference),
      };
    },
    scheduleMapRender() {
      this.$nextTick(() => this.renderNumberedMap());
    },
    renderNumberedMap() {
      if (!this.$refs.journeyMap || this.mapView !== 'stops' || !this.hasMappedStops) return;
      if (!this.journeyMapInstance) {
        this.journeyMapInstance = L.map(this.$refs.journeyMap, {
          scrollWheelZoom: false,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(this.journeyMapInstance);
      }
      if (this.journeyStopsLayer) this.journeyStopsLayer.remove();
      this.journeyStopsLayer = L.layerGroup().addTo(this.journeyMapInstance);
      const points = [];
      const coordinateCounts = (this.activeDay?.items || []).reduce((counts, item) => {
        if (!Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) return counts;
        const key = `${item.latitude},${item.longitude}`;
        return { ...counts, [key]: (counts[key] || 0) + 1 };
      }, {});
      const coordinatePositions = {};
      (this.activeDay?.items || []).forEach((item, index) => {
        if (!Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) return;
        const point = [item.latitude, item.longitude];
        points.push(point);
        const coordinateKey = `${item.latitude},${item.longitude}`;
        const coordinatePosition = coordinatePositions[coordinateKey] || 0;
        coordinatePositions[coordinateKey] = coordinatePosition + 1;
        const markerOffset = (coordinatePosition - ((coordinateCounts[coordinateKey] - 1) / 2)) * 32;
        const activeClass = index === this.activeItemIndex ? ' active' : '';
        const icon = L.divIcon({
          className: 'journey-marker-shell',
          html: `<span class="journey-marker${activeClass}">${index + 1}</span>`,
          iconAnchor: [15 - markerOffset, 15],
          iconSize: [30, 30],
        });
        L.marker(point, { icon, keyboard: true, title: `${index + 1}. ${item.title}` })
          .on('click', () => this.selectItem(index))
          .addTo(this.journeyStopsLayer);
      });
      if (points.length > 1) {
        this.journeyMapInstance.fitBounds(points, { maxZoom: 16, padding: [48, 48] });
      } else if (points.length === 1) {
        this.journeyMapInstance.setView(points[0], 16);
      }
      this.journeyMapInstance.invalidateSize();
    },
    shortDayLabel(item) {
      const date = item.journeyDate || item.startDate;
      if (!date) return item.dayLabel || 'Journey';
      return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'short', timeZone: 'UTC',
      });
    },
    shortDateLabel(item) {
      const date = item.journeyDate || item.startDate;
      if (!date) return '';
      return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'UTC',
      });
    },
    fullDayLabel(item) {
      const date = item.journeyDate || item.startDate;
      if (!date) return item.dayLabel || 'Journey';
      return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      });
    },
    selectDay(index) {
      if (this.days[index]?.disabled) return;
      this.activeDayIndex = index;
      this.activeItemIndex = 0;
      this.mapView = 'stops';
    },
    selectItem(index) { this.activeItemIndex = index; },
    enabledDayIndex(startIndex, step) {
      let index = startIndex + step;
      while (index >= 0 && index < this.days.length) {
        if (!this.days[index].disabled) return index;
        index += step;
      }
      return -1;
    },
    goPrevious() {
      if (this.activeItemIndex > 0) {
        this.activeItemIndex -= 1;
      } else {
        const previousDayIndex = this.enabledDayIndex(this.activeDayIndex, -1);
        if (previousDayIndex < 0) return;
        this.activeDayIndex = previousDayIndex;
        this.activeItemIndex = this.days[this.activeDayIndex].items.length - 1;
      }
    },
    goNext() {
      if (this.activeItemIndex < this.activeDay.items.length - 1) {
        this.activeItemIndex += 1;
      } else {
        const nextDayIndex = this.enabledDayIndex(this.activeDayIndex, 1);
        if (nextDayIndex < 0) return;
        this.activeDayIndex = nextDayIndex;
        this.activeItemIndex = 0;
      }
    },
    selectFromHash() {
      const id = (this.$route.hash || '').replace(/^#story-/, '');
      if (!id) return;
      this.days.some((day, dayIndex) => {
        if (day.disabled) return false;
        const itemIndex = day.items.findIndex((item) => item.id === id);
        if (itemIndex < 0) return false;
        this.activeDayIndex = dayIndex;
        this.activeItemIndex = itemIndex;
        this.$nextTick(() => {
          document.getElementById('story-timeline')?.scrollIntoView({ block: 'start' });
        });
        return true;
      });
    },
  },
};
</script>

<style scoped>
.story-timeline { background: #0b0b0b; border-bottom: 1px solid #282828; border-top: 1px solid #282828; padding: 76px 0; scroll-margin-top: 68px; }
.active-story.draft-preview { background: linear-gradient(145deg, rgba(240,177,60,.14), #111 55%); border-color: rgba(240,177,60,.72); }
.section-kicker { color: #e23232; font-family: "Oswald", sans-serif; font-weight: 800; letter-spacing: .1em; margin: 0 0 8px; text-transform: uppercase; }
h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(2rem, 5vw, 3.2rem); line-height: 1; margin: 0; }
.section-description { color: #aaa; font-size: 1.08rem; line-height: 1.6; margin: 16px 0 0; max-width: 760px; }
.day-tabs { display: flex; gap: 8px; margin-top: 30px; overflow-x: auto; padding: 0 0 8px; }
.day-tabs button { background: #141414; border: 1px solid #333; border-radius: 8px; color: #aaa; flex: 0 0 auto; min-width: 88px; padding: 10px 15px; text-align: left; transition: background 160ms ease, border-color 160ms ease; }
.day-tabs button:not(:disabled):hover, .day-tabs button:not(:disabled):focus, .day-tabs button.active { background: #21150c; border-color: #f0b13c; color: #fff; outline: none; }
.day-tabs button:disabled { cursor: default; opacity: .35; }
.day-tabs strong, .day-tabs span { display: block; }
.day-tabs strong { color: inherit; font-family: "Oswald", sans-serif; font-size: 1rem; text-transform: uppercase; }
.day-tabs span { color: #999; font-size: .72rem; margin-top: 2px; }
.journey-viewer { background: #111; border: 1px solid #303030; border-radius: 14px; display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(300px, .65fr); margin-top: 14px; min-height: 430px; overflow: hidden; }
.map-panel { display: flex; flex-direction: column; min-height: 430px; }
.map-toolbar { align-items: center; background: #0c0c0c; border-bottom: 1px solid #303030; display: flex; justify-content: flex-end; min-height: 45px; padding: 5px 8px; }
.map-stage { flex: 1 1 auto; min-height: 0; position: relative; }
.map-stage iframe, .numbered-map { height: 100%; inset: 0; position: absolute; width: 100%; }
.map-panel iframe { border: 0; filter: grayscale(.75) invert(.92) contrast(.88); }
.numbered-map { background: #181818; z-index: 1; }
.numbered-map::v-deep .leaflet-tile-pane { filter: grayscale(.78) invert(.92) contrast(.88); }
.numbered-map::v-deep .leaflet-control-zoom a { background: #111; border-color: #333; color: #fff; }
.numbered-map::v-deep .leaflet-control-attribution { background: rgba(8,8,8,.78); color: #aaa; }
.numbered-map::v-deep .leaflet-control-attribution a { color: #f0b13c; }
.numbered-map::v-deep .journey-marker-shell { background: transparent; border: 0; }
.numbered-map::v-deep .journey-marker { align-items: center; background: #e23232; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,.75); color: #fff; display: flex; font-family: "Oswald", sans-serif; font-size: .72rem; font-weight: 900; height: 29px; justify-content: center; width: 29px; }
.numbered-map::v-deep .journey-marker.active { background: #f0b13c; color: #111; outline: 3px solid rgba(240,177,60,.35); }
.map-view-toggle { background: #151515; border: 1px solid #4a3825; border-radius: 7px; display: flex; gap: 3px; padding: 3px; }
.map-view-toggle button { background: transparent; border: 0; border-radius: 5px; color: #aaa; font-size: .7rem; font-weight: 800; padding: 7px 9px; text-transform: uppercase; }
.map-view-toggle button:hover, .map-view-toggle button:focus, .map-view-toggle button.active { background: #e23232; color: #fff; outline: none; }
.direction-segments { background: #101010; border-bottom: 1px solid #303030; display: flex; gap: 6px; overflow-x: auto; padding: 7px 8px; }
.direction-segments button { background: #181818; border: 1px solid #383838; border-radius: 999px; color: #aaa; flex: 0 0 auto; font-size: .66rem; font-weight: 800; max-width: 260px; overflow: hidden; padding: 7px 11px; text-overflow: ellipsis; white-space: nowrap; }
.direction-segments button::before { background: #e23232; border-radius: 50%; content: ''; display: inline-block; height: 7px; margin-right: 7px; width: 7px; }
.direction-segments button.mode-driving::before { background: #f0b13c; }
.direction-segments button.mode-ferry::before { background: #45c7d8; }
.direction-segments button.mode-transit::before { background: #a68cff; }
.direction-segments button.mode-bicycling::before { background: #79b8ff; }
.direction-segments button:hover, .direction-segments button:focus, .direction-segments button.active { border-color: #f0b13c; color: #fff; outline: none; }
.unmapped-stops { align-items: center; background: rgba(5,5,5,.9); border: 1px solid #4a3825; border-radius: 7px; color: #aaa; display: flex; font-size: .68rem; gap: 5px; padding: 6px 8px; position: absolute; right: 14px; top: 14px; z-index: 4; }
.unmapped-stops button { align-items: center; background: #e23232; border: 1px solid #fff; border-radius: 50%; color: #fff; display: flex; font-size: .65rem; height: 24px; justify-content: center; width: 24px; }
.map-label { align-items: center; backdrop-filter: blur(8px); background: rgba(5,5,5,.86); border: 1px solid #4a3825; border-radius: 8px; bottom: 16px; color: #ddd; display: flex; font-size: .8rem; gap: 6px; left: 16px; max-width: calc(100% - 32px); padding: 9px 12px; position: absolute; }
.map-label .v-icon { color: #f0b13c; }
.active-story { display: flex; flex-direction: column; padding: 30px; }
.story-progress { color: #8d8d8d; display: flex; flex-wrap: wrap; font-size: .72rem; gap: 8px 14px; justify-content: space-between; letter-spacing: .05em; text-transform: uppercase; }
.story-type { color: #e85a5a; font-family: "Oswald", sans-serif; font-size: .78rem; font-weight: 900; letter-spacing: .09em; margin: 44px 0 8px; text-transform: uppercase; }
.active-story h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(1.8rem, 3.5vw, 2.6rem); line-height: 1.05; margin: 0; }
.story-summary { -webkit-box-orient: vertical; -webkit-line-clamp: 6; color: #bbb; display: -webkit-box; line-height: 1.6; margin: 16px 0 0; overflow: hidden; }
.story-links { align-items: center; display: flex; flex-wrap: wrap; gap: 10px 18px; margin-top: 22px; }
.story-link { align-items: center; color: #f0b13c; display: inline-flex; font-size: .8rem; font-weight: 900; gap: 5px; text-decoration: none; text-transform: uppercase; }
.story-link .v-icon { color: inherit; }
.journey-controls { display: flex; gap: 9px; margin-top: auto; padding-top: 28px; }
.journey-controls button { align-items: center; background: #1a1a1a; border: 1px solid #3a3a3a; border-radius: 7px; color: #fff; display: inline-flex; font-size: .76rem; font-weight: 800; gap: 5px; justify-content: center; padding: 10px 12px; text-transform: uppercase; }
.journey-controls button:last-child { background: #e23232; border-color: #e23232; margin-left: auto; }
.journey-controls button:disabled { cursor: default; opacity: .35; }
.journey-controls .v-icon { color: inherit; }
.day-route { display: flex; gap: 7px; list-style: none; margin: 14px 0 0; overflow-x: auto; padding: 0 0 8px; }
.day-route li { flex: 0 0 auto; }
.day-route button { align-items: center; background: transparent; border: 1px solid #303030; border-radius: 999px; color: #999; display: flex; gap: 8px; max-width: 230px; padding: 7px 12px 7px 7px; scroll-margin-top: 90px; }
.day-route button:hover, .day-route button:focus, .day-route button.active, .day-route button:target { border-color: #e23232; color: #fff; outline: none; }
.day-route button > span { align-items: center; background: #26170f; border-radius: 50%; color: #f0b13c; display: flex; flex: 0 0 auto; font-family: "Oswald", sans-serif; font-size: .68rem; height: 28px; justify-content: center; width: 28px; }
.day-route strong { font-size: .75rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 850px) { .journey-viewer { grid-template-columns: 1fr; } .map-panel { min-height: 320px; } .active-story { min-height: 330px; } }
@media (max-width: 600px) { .story-timeline { padding: 58px 0; } .journey-viewer { min-height: 0; } .map-panel { min-height: 340px; } .map-toolbar { min-height: 41px; padding: 4px 6px; } .map-view-toggle button { font-size: .62rem; padding: 6px; } .active-story { min-height: 350px; padding: 23px; } .story-type { margin-top: 30px; } }
</style>
